import {
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type ITraceSourceRowProps from "./type";
import type {
    TraceSourceRowKeyboardEventType,
    TraceSourceRowWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import triggerStylesheet, {
    useStyles as useTriggerStyles
} from "../../components/trigger/stylesheet";
import {
    ArrowUpRight as ArrowUpRightIcon
} from "lucide-react-native";
import {
    getMirrorStyle
} from "../../../../helpers/direction";
import {
    getSourceDomain
} from "../../../citation/util";
import {
    getTraceLineColors
} from "../../stylesheet";
import {
    getTraceRowHeight
} from "../../util";
import Text from "../../../text";

const TraceSourceRow: FC<ITraceSourceRowProps> = ({
    customLocalize,
    customTheme,
    onPress,
    source,
    index,
    style
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState();

    const isActive = isHovered || isPressed;

    const currentColors = getTraceLineColors({
        status: "done",
        isActive,
        colors
    });

    const rowHeight = getTraceRowHeight({
        isInteractive: true,
        controlHeights,
        touchTargets,
        pointerType
    });

    const {
        container: containerDynamicStyle,
        layer: layerDynamicStyle
    } = useTriggerStyles({
        controlHeights,
        isFocusVisible,
        shapeConfigs,
        isHovered,
        isPressed,
        rowHeight,
        radiuses,
        borders,
        colors,
        spaces
    });

    const {
        description: descriptionDynamicStyle,
        index: indexDynamicStyle,
        line: lineDynamicStyle
    } = useStyles({
        shapeConfigs,
        iconSizes,
        radiuses,
        colors,
        spaces
    });

    const domain = getSourceDomain({
        source
    });

    const handleKeyDown = (event: TraceSourceRowKeyboardEventType) => {
        if(event.key !== "Enter" && event.key !== " ") {
            return;
        }

        event.preventDefault();

        onPress(source);
    };

    const webProps: TraceSourceRowWebPropsType = Platform.OS === "web" ? {
        onKeyDown: handleKeyDown
    } : {};

    return <Pressable
        {...interactionHandlers}
        {...webProps}
        accessibilityLabel={`${source.title}, ${domain} ${localize("opens-in-new-tab")}`}
        style={[
            style,
            triggerStylesheet.container,
            containerDynamicStyle
        ]}
        onPress={() => onPress(source)}
        role="link"
    >
        <View
            style={[
                triggerStylesheet.layer,
                layerDynamicStyle
            ]}
            aria-hidden={true}
        />
        <View
            style={[
                stylesheet.line,
                lineDynamicStyle
            ]}
        >
            <View
                style={[
                    stylesheet.index,
                    indexDynamicStyle
                ]}
                aria-hidden={true}
            >
                <Text
                    style={stylesheet.indexText}
                    customTheme={customTheme}
                    variant="labelSmallSize"
                    color="mid"
                >
                    {`${index}`}
                </Text>
            </View>
            <Text
                customColor={currentColors.titleColor}
                customTheme={customTheme}
                style={stylesheet.title}
                variant="bodyMediumSize"
                numberOfLines={1}
            >
                {source.title}
            </Text>
            <Text
                customColor={currentColors.descriptionColor}
                style={[
                    stylesheet.description,
                    descriptionDynamicStyle
                ]}
                customTheme={customTheme}
                variant="bodyMediumSize"
                numberOfLines={1}
            >
                {domain}
            </Text>
        </View>
        <View
            style={[
                stylesheet.end,
                getMirrorStyle({
                    direction
                })
            ]}
            aria-hidden={true}
        >
            <ArrowUpRightIcon
                color={colors.content.icon.low}
                size={iconSizes.small}
            />
        </View>
    </Pressable>;
};
export default TraceSourceRow;
